import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import api, { clientWorkTasksAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate, parseDateOnlyLocalMs } from '../utils/dateFormat';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './ClientWorkTasks.css';

const SORT_KEYS = {
  kind: 'kind',
  title_types: 'title_types',
  status: 'status',
  due_requested: 'due_requested',
  received_date: 'received_date',
  created_date: 'created_date',
};

function statusRank(status) {
  if (status === 'open') return 0;
  if (status === 'done') return 1;
  if (status === 'canceled') return 2;
  return 3;
}

function dateFieldMs(value) {
  if (value == null || value === '') return null;
  const ms = parseDateOnlyLocalMs(value);
  return ms || null;
}

function dueRequestedMs(task) {
  if (task.kind === 'general') return dateFieldMs(task.due_date);
  return dateFieldMs(task.requested_date || task.due_date);
}

function receivedMs(task) {
  if (task.kind !== 'work_task') return null;
  return dateFieldMs(task.received_date);
}

function createdRecordMs(task) {
  return dateFieldMs(task.created_date);
}

function kindLabel(task) {
  return task.kind === 'work_task' ? 'Work task' : 'General';
}

function titleTypesLabel(task) {
  if (task.kind === 'work_task') {
    return `${task.work_type_name ?? '—'} – ${task.task_type_name ?? '—'}`;
  }
  return task.title || '';
}

/** Date columns: nulls always last; asc = oldest first, desc = newest first. */
function compareNullableDateMs(aMs, bMs, sortDir) {
  const aN = aMs == null;
  const bN = bMs == null;
  if (aN && bN) return 0;
  if (aN) return 1;
  if (bN) return -1;
  const raw = aMs - bMs;
  return sortDir === 'asc' ? raw : -raw;
}

function compareClientTasks(a, b, sortKey, sortDir) {
  let cmp = 0;
  switch (sortKey) {
    case SORT_KEYS.kind:
      cmp = kindLabel(a).localeCompare(kindLabel(b), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    case SORT_KEYS.title_types:
      cmp = titleTypesLabel(a).localeCompare(titleTypesLabel(b), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    case SORT_KEYS.status:
      cmp = statusRank(a.status) - statusRank(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    case SORT_KEYS.due_requested:
      return compareNullableDateMs(dueRequestedMs(a), dueRequestedMs(b), sortDir);
    case SORT_KEYS.received_date:
      return compareNullableDateMs(receivedMs(a), receivedMs(b), sortDir);
    case SORT_KEYS.created_date:
      return compareNullableDateMs(createdRecordMs(a), createdRecordMs(b), sortDir);
    default:
      return 0;
  }
}

function emptyForm() {
  return {
    kind: 'general',
    title: '',
    description: '',
    due_date: '',
    visible_from_date: '',
    work_type_id: '',
    task_type_id: '',
    created_date: new Date().toISOString().slice(0, 10),
    requested_date: '',
    received_date: '',
    note: '',
    status: 'open',
    repeat_interval: 'none',
    repeat_end_on: '',
    repeat_max_occurrences: '',
  };
}

function addCalendarDaysLocal(days) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultOneYearFromTodayLocal() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function repeatLabel(interval) {
  if (interval === 'weekly') return 'Weekly';
  if (interval === 'monthly') return 'Monthly';
  if (interval === 'quarterly') return 'Quarterly';
  return '—';
}

function ClientWorkTasks({ referralIdOverride, openTaskId, onOpenTaskHandled }) {
  const outletContext = useOutletContext();
  const referralId = referralIdOverride ?? outletContext?.currentReferral?.id;
  const [tasks, setTasks] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortKey, setSortKey] = useState(SORT_KEYS.created_date);
  const [sortDir, setSortDir] = useState('desc');
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [followUpChecked, setFollowUpChecked] = useState(false);
  const [followUpDueDate, setFollowUpDueDate] = useState(() => addCalendarDaysLocal(30));
  const openedTaskIdRef = useRef(null);
  const statusWhenEditStartedRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const canCreate = hasPermission('client_work_tasks.create');
  const canUpdate = hasPermission('client_work_tasks.update');
  const canDelete = hasPermission('client_work_tasks.delete');

  useEffect(() => {
    if (!referralId) return;
    loadAll();
  }, [referralId, showCompleted]);

  useEffect(() => {
    api.get('/whoami').then((res) => {
      const uid = res.data?.user?.id ?? null;
      setCurrentUserId(uid != null ? String(uid) : null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!openTaskId || loading || tasks.length === 0 || !onOpenTaskHandled) return;
    if (openedTaskIdRef.current === openTaskId) return;
    const task = tasks.find((t) => String(t.id) === String(openTaskId));
    if (task) {
      openedTaskIdRef.current = openTaskId;
      statusWhenEditStartedRef.current = task.status || 'open';
      setFormData(taskToForm(task));
      setEditingId(task.id);
      setShowForm(true);
      onOpenTaskHandled();
    }
  }, [openTaskId, loading, tasks, onOpenTaskHandled]);

  function taskToForm(task) {
    const kind = task.kind === 'work_task' ? 'work_task' : 'general';
    return {
      kind,
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date || '',
      visible_from_date: task.visible_from_date || '',
      work_type_id: task.work_type_id || '',
      task_type_id: task.task_type_id || '',
      created_date: task.created_date || new Date().toISOString().slice(0, 10),
      requested_date: task.requested_date || '',
      received_date: task.received_date || '',
      note: task.note || '',
      status: task.status || 'open',
      repeat_interval: task.repeat_interval === 'weekly' || task.repeat_interval === 'monthly' || task.repeat_interval === 'quarterly' ? task.repeat_interval : 'none',
      repeat_end_on: task.repeat_end_on || '',
      repeat_max_occurrences:
        task.repeat_max_occurrences != null && task.repeat_max_occurrences !== '' ? String(task.repeat_max_occurrences) : '',
    };
  }

  const loadAll = async () => {
    if (!referralId) return;
    setLoading(true);
    setError(null);
    try {
      const [tasksRes, workRes, taskRes] = await Promise.all([
        clientWorkTasksAPI.getByReferralId(referralId, {
          params: showCompleted ? { includeCompleted: 'true' } : {},
        }),
        clientWorkTasksAPI.getWorkTypes(),
        clientWorkTasksAPI.getTaskTypes(),
      ]);
      setTasks(tasksRes.data?.data ?? []);
      setWorkTypes(workRes.data?.data ?? []);
      setTaskTypes(taskRes.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load My To-Dos');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingId(null);
    setShowForm(false);
    statusWhenEditStartedRef.current = null;
  };

  const handleAdd = () => {
    setFormData(emptyForm());
    setEditingId(null);
    statusWhenEditStartedRef.current = null;
    setShowForm(true);
  };

  const handleEdit = (task) => {
    setFormData(taskToForm(task));
    statusWhenEditStartedRef.current = task.status || 'open';
    setEditingId(task.id);
    setShowForm(true);
  };

  const buildRepeatPayload = () => {
    const rep = formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly'
      ? formData.repeat_interval
      : 'none';
    const maxRaw = formData.repeat_max_occurrences?.trim();
    let repeat_max_occurrences = maxRaw ? parseInt(maxRaw, 10) : null;
    if (Number.isNaN(repeat_max_occurrences)) repeat_max_occurrences = null;
    return {
      repeat_interval: rep,
      repeat_end_on: rep !== 'none' && formData.repeat_end_on ? formData.repeat_end_on : null,
      repeat_max_occurrences: rep !== 'none' ? repeat_max_occurrences : null,
    };
  };

  const performSave = async (completeOpts) => {
    if (!referralId) return;
    const k = formData.kind === 'work_task' ? 'work_task' : 'general';
    let payload;
    if (k === 'general') {
      payload = {
        kind: 'general',
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        due_date: formData.due_date || null,
        visible_from_date: formData.visible_from_date || null,
        ...buildRepeatPayload(),
      };
    } else {
      payload = {
        kind: 'work_task',
        work_type_id: formData.work_type_id,
        task_type_id: formData.task_type_id,
        created_date: formData.created_date || null,
        requested_date: formData.requested_date || null,
        received_date: formData.received_date || null,
        note: formData.note?.trim() || null,
        due_date: formData.due_date || null,
        ...buildRepeatPayload(),
      };
    }
    if (editingId) {
      payload.status = formData.status;
      if (completeOpts?.create_follow_up) {
        payload.create_follow_up = true;
        payload.follow_up_due_date = completeOpts.follow_up_due_date || null;
      }
      await clientWorkTasksAPI.update(referralId, editingId, payload);
    } else {
      await clientWorkTasksAPI.create(referralId, payload);
    }
    await loadAll();
    resetForm();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!referralId) return;
    const k = formData.kind === 'work_task' ? 'work_task' : 'general';
    if (k === 'general') {
      if (!formData.title?.trim()) {
        alert('Title is required.');
        return;
      }
    } else if (!formData.work_type_id || !formData.task_type_id) {
      alert('Work type and task type are required for a work-task to-do.');
      return;
    }
    const rep = formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly';
    if (rep) {
      if (k === 'general' && !formData.due_date) {
        alert('Due date is required when repeat is set.');
        return;
      }
      if (k === 'work_task' && !formData.requested_date && !formData.due_date) {
        alert('Requested date or due date is required when repeat is set.');
        return;
      }
    }
    if (
      editingId
      && formData.status === 'done'
      && statusWhenEditStartedRef.current === 'open'
    ) {
      setFollowUpChecked(false);
      setFollowUpDueDate(addCalendarDaysLocal(30));
      setCompleteModalOpen(true);
      return;
    }
    setSaving(true);
    try {
      await performSave(null);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const confirmCompleteAndSave = async () => {
    setCompleteModalOpen(false);
    setSaving(true);
    try {
      await performSave({
        create_follow_up: followUpChecked,
        follow_up_due_date: followUpChecked ? followUpDueDate : null,
      });
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancelCompleteModal = () => {
    setCompleteModalOpen(false);
  };

  const handleDelete = async (taskId) => {
    if (!referralId || !window.confirm('Remove this to-do?')) return;
    try {
      await clientWorkTasksAPI.delete(referralId, taskId);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete');
    }
  };

  const sortedTasks = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => compareClientTasks(a, b, sortKey, sortDir));
    return copy;
  }, [tasks, sortKey, sortDir]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const dateKeys = [
        SORT_KEYS.due_requested,
        SORT_KEYS.received_date,
        SORT_KEYS.created_date,
      ];
      setSortDir(dateKeys.includes(key) ? 'desc' : 'asc');
    }
  };

  if (!referralId) {
    return (
      <div className="client-work-tasks">
        No active referral. My To-Dos are tied to the current episode of care.
      </div>
    );
  }

  const actions = canCreate && !showForm ? (
    <button type="button" className="btn-add-work-task" onClick={handleAdd}>
      + Add To-Do
    </button>
  ) : null;

  return (
    <div className="client-work-tasks">
      <ContentHeader
        title="My To-Dos"
        titleHint="General reminders or work-task items (work and task types) for this episode of care. Use the filter below to show Done and Canceled items. For reminders not tied to a client, use My To-Dos in the left navigation."
        actions={actions}
      />

      <div className="client-work-tasks-toolbar">
        <label className="client-work-tasks-show-done">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
          Show Done &amp; Canceled
        </label>
      </div>

      {error && <p className="client-work-tasks-error">{error}</p>}
      {loading && <p className="client-work-tasks-loading">Loading…</p>}

      {showForm && (
        <div className="client-work-tasks-form">
          <h3>{editingId ? 'Edit To-Do' : 'Add To-Do'}</h3>
          <form onSubmit={handleSave}>
            <div className="client-work-tasks-form-top-row">
              <div className="form-group client-work-tasks-form-type-field">
                <label htmlFor="todo-kind">Type</label>
                <select
                  id="todo-kind"
                  value={formData.kind}
                  onChange={(e) => setFormData({ ...formData, kind: e.target.value, work_type_id: '', task_type_id: '' })}
                  disabled={!!editingId}
                >
                  <option value="general">General</option>
                  <option value="work_task">Work task (types)</option>
                </select>
              </div>
              {editingId && (
                <div className="form-actions form-actions--top">
                  <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              )}
            </div>

            {formData.kind === 'general' && (
              <>
                <div className="client-work-tasks-form-grid">
                  <div className="form-group form-group-full">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      maxLength={500}
                      required
                    />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Description</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional details"
                    />
                  </div>
                  <div className="form-group">
                    <label>Due date</label>
                    <input
                      type="date"
                      value={formData.due_date || ''}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Visible from</label>
                    <input
                      type="date"
                      value={formData.visible_from_date || ''}
                      onChange={(e) => setFormData({ ...formData, visible_from_date: e.target.value })}
                      title="Item appears on your dashboard on or after this date"
                    />
                  </div>
                </div>
              </>
            )}

            {formData.kind === 'work_task' && (
              <div className="client-work-tasks-form-grid client-work-tasks-form-grid--3col">
                <div className="form-group">
                  <label>Work type *</label>
                  <select
                    value={formData.work_type_id}
                    onChange={(e) => setFormData({ ...formData, work_type_id: e.target.value, task_type_id: '' })}
                    required={formData.kind === 'work_task'}
                  >
                    <option value="">Select…</option>
                    {workTypes.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Task type *</label>
                  <select
                    value={formData.task_type_id}
                    onChange={(e) => setFormData({ ...formData, task_type_id: e.target.value })}
                    required={formData.kind === 'work_task'}
                    disabled={!formData.work_type_id}
                  >
                    <option value="">Select…</option>
                    {taskTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Requested</label>
                  <input
                    type="date"
                    value={formData.requested_date || ''}
                    onChange={(e) => setFormData({ ...formData, requested_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Due date</label>
                  <input
                    type="date"
                    value={formData.due_date || ''}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    title="Optional; use with repeat if you want a due date in addition to requested"
                  />
                </div>
                <div className="form-group">
                  <label>Received</label>
                  <input
                    type="date"
                    value={formData.received_date || ''}
                    onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Created (record)</label>
                  <input
                    type="date"
                    value={formData.created_date || ''}
                    onChange={(e) => setFormData({ ...formData, created_date: e.target.value })}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Note</label>
                  <textarea
                    rows={2}
                    value={formData.note || ''}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    maxLength={4000}
                    placeholder="Optional note"
                    className="cwt-note-textarea"
                  />
                </div>
              </div>
            )}

            <div className="form-group form-group-full" style={{ marginTop: 8 }}>
              <label htmlFor="cwt-repeat-interval">Repeat</label>
              <select
                id="cwt-repeat-interval"
                value={formData.repeat_interval}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const next = { ...prev, repeat_interval: v };
                    if ((v === 'weekly' || v === 'monthly' || v === 'quarterly') && !prev.repeat_end_on) {
                      next.repeat_end_on = defaultOneYearFromTodayLocal();
                    }
                    if (v === 'none') {
                      next.repeat_end_on = '';
                      next.repeat_max_occurrences = '';
                    }
                    return next;
                  });
                }}
              >
                <option value="none">None</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
              <p className="client-work-tasks-repeat-help">
                Next due is based on the completion date plus the interval. Defaults to ending one year from when repeat is set; optional cap after N completions.
              </p>
            </div>
            {(formData.repeat_interval === 'weekly' || formData.repeat_interval === 'monthly' || formData.repeat_interval === 'quarterly') && (
              <div className="client-work-tasks-form-grid">
                <div className="form-group">
                  <label>Series end date</label>
                  <input
                    type="date"
                    value={formData.repeat_end_on || ''}
                    onChange={(e) => setFormData({ ...formData, repeat_end_on: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End after N completions</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={formData.repeat_max_occurrences}
                    onChange={(e) => setFormData({ ...formData, repeat_max_occurrences: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}

            {editingId && (
              <div className="form-group form-group-full" style={{ marginTop: 12 }}>
                <label htmlFor="todo-status">Status</label>
                <select
                  id="todo-status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="done">Done</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {!loading && !error && (
        <div className="client-work-tasks-table-wrap">
          {tasks.length === 0 ? (
            <p className="no-work-tasks">
              No to-dos yet.
              {canCreate && ' Click “Add To-Do” to add one.'}
            </p>
          ) : (
            <table className="client-work-tasks-table">
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.kind)}
                    >
                      Kind
                      {sortKey === SORT_KEYS.kind ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.title_types)}
                    >
                      Title / types
                      {sortKey === SORT_KEYS.title_types ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.status)}
                    >
                      Status
                      {sortKey === SORT_KEYS.status ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.due_requested)}
                    >
                      Due / requested
                      {sortKey === SORT_KEYS.due_requested ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>Repeat</th>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.received_date)}
                    >
                      Received
                      {sortKey === SORT_KEYS.received_date ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="client-work-tasks-sort-btn"
                      onClick={() => handleSortClick(SORT_KEYS.created_date)}
                    >
                      Created
                      {sortKey === SORT_KEYS.created_date ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                  <th className="th-note" title="Has note">Note</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => (
                  <tr
                    key={task.id}
                    className={canUpdate ? 'client-work-tasks-row-clickable' : ''}
                    onClick={canUpdate ? () => handleEdit(task) : undefined}
                    role={canUpdate ? 'button' : undefined}
                    tabIndex={canUpdate ? 0 : undefined}
                    onKeyDown={canUpdate ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(task); } } : undefined}
                  >
                    <td>{task.kind === 'work_task' ? 'Work task' : 'General'}</td>
                    <td className="todo-title-cell">
                      {task.kind === 'work_task'
                        ? `${task.work_type_name ?? '—'} – ${task.task_type_name ?? '—'}`
                        : (task.title || '—')}
                      {task.owner_user_id && currentUserId && String(task.owner_user_id) !== currentUserId && (
                        <span className="todo-added-by-badge" title={`Added by ${[task.creator_first_name, task.creator_last_name].filter(Boolean).join(' ') || 'another user'}`}>
                          Added by {[task.creator_first_name, task.creator_last_name].filter(Boolean).join(' ') || 'someone'}
                        </span>
                      )}
                    </td>
                    <td>{task.status === 'open' ? 'Open' : task.status === 'done' ? 'Done' : 'Canceled'}</td>
                    <td>
                      {task.kind === 'general'
                        ? formatDisplayDate(task.due_date)
                        : formatDisplayDate(task.requested_date || task.due_date)}
                    </td>
                    <td>{repeatLabel(task.repeat_interval)}</td>
                    <td>{task.kind === 'work_task' ? formatDisplayDate(task.received_date) : '—'}</td>
                    <td>{formatDisplayDate(task.created_date)}</td>
                    <td className="note-cell" title={task.note || undefined}>
                      {task.note ? '📝' : '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canDelete && (
                        <DeleteIconButton className="btn-delete-small" onClick={() => handleDelete(task.id)} title="Remove" aria-label="Remove" />
                      )}
                      {!canDelete && <span className="no-actions">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {completeModalOpen && (
        <div
          className="client-work-tasks-modal-overlay"
          role="presentation"
          onClick={cancelCompleteModal}
          onKeyDown={(e) => { if (e.key === 'Escape') cancelCompleteModal(); }}
        >
          <div
            className="client-work-tasks-modal"
            role="dialog"
            aria-labelledby="cwt-complete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cwt-complete-title">Mark done</h3>
            <p className="client-work-tasks-modal-lead">
              Create a one-time follow-up with the same episode context? Default due date is 30 days from today.
            </p>
            <label className="client-work-tasks-modal-check">
              <input
                type="checkbox"
                checked={followUpChecked}
                onChange={(e) => setFollowUpChecked(e.target.checked)}
              />
              Create follow-up
            </label>
            {followUpChecked && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label htmlFor="cwt-follow-due">Follow-up due date</label>
                <input
                  id="cwt-follow-due"
                  type="date"
                  value={followUpDueDate}
                  onChange={(e) => setFollowUpDueDate(e.target.value)}
                />
              </div>
            )}
            <div className="client-work-tasks-modal-actions">
              <button type="button" className="btn-cancel" onClick={cancelCompleteModal}>Back</button>
              <button type="button" className="btn-save" onClick={confirmCompleteAndSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save as done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientWorkTasks;
